import * as React from 'react';
import Layout from '../../components/layout';
import { db } from '../../firebaseconf';
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    orderBy,
    limit
} from 'firebase/firestore';
import Header from '../../components/header';
import {
    Grid,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
    useTheme,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Link } from 'react-router-dom';
import StatIcon from '@mui/icons-material/BarChart';
import StudentIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/AssignmentTurnedIn';
import ClassIcon from '@mui/icons-material/Class';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

function TeacherDashboard() {
    const [teacherData, setTeacherData] = React.useState(null);
    const [students, setStudents] = React.useState([]);
    const [classes, setClasses] = React.useState([]);
    const [assignments, setAssignments] = React.useState([]);
    const [stats, setStats] = React.useState({
        totalStudents: 0,
        totalClasses: 0,
        totalAssignments: 0,
        pendingSubmissions: 0,
    });
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const cards = [
        { id: 1, title: 'My Classes', description: 'View assigned classes.', slug: '/my-classes', icon: <ClassIcon sx={{ fontSize: 40 }} /> },
        { id: 2, title: 'Create Assignment', description: 'Post new assignments.', slug: '/assignment', icon: <AssignmentIcon sx={{ fontSize: 40 }} /> },
        { id: 3, title: 'Mark Attendance', description: 'Update student attendance.', slug: '/addAttendence', icon: <StatIcon sx={{ fontSize: 40 }} /> },
    ];

    React.useEffect(() => {
        const fetchTeacherData = async () => {
            try {
                setLoading(true);
                const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
                
                if (!loggedUser || !loggedUser.institution_id || loggedUser.role !== 'teacher') {
                    setError('Invalid session. Please login again.');
                    setLoading(false);
                    return;
                }

                const instId = loggedUser.institution_id;
                const teacherId = loggedUser.id;

                // Set teacher data from logged user
                setTeacherData({
                    id: loggedUser.id,
                    name: loggedUser.name,
                    email: loggedUser.email,
                    phone: loggedUser.phone,
                    avatar: loggedUser.avatar || 'https://i.pravatar.cc/150?img=1',
                    subjects: loggedUser.subjects || [],
                    classes: loggedUser.classes || [],
                    department: loggedUser.department || 'Science',
                });

                // Fetch teacher's classes
                const classesRef = collection(db, 'institutions', instId, 'classes');
                const classesQuery = query(classesRef, where('teacher_id', '==', teacherId));
                const classSnapshot = await getDocs(classesQuery);
                const classesData = classSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setClasses(classesData);
                setStats(prev => ({ ...prev, totalClasses: classesData.length }));

                // Fetch students in those classes
                if (classesData.length > 0) {
                    const classNames = classesData.map(c => c.name);
                    const studentsRef = collection(db, 'institutions', instId, 'students');
                    const studentsQuery = query(
                        studentsRef,
                        where('class', 'in', classNames)
                    );
                    const studentSnapshot = await getDocs(studentsQuery);
                    const studentsData = studentSnapshot.docs.map(doc => ({ 
                        id: doc.id, 
                        ...doc.data() 
                    }));
                    setStudents(studentsData);
                    setStats(prev => ({ ...prev, totalStudents: studentsData.length }));
                }

                // Fetch assignments for this teacher
                const assignmentsRef = collection(db, 'institutions', instId, 'assignments');
                const assignmentsQuery = query(
                    assignmentsRef,
                    where('teacher_id', '==', teacherId),
                    orderBy('createdAt', 'desc'),
                    limit(10)
                );
                const assignmentsSnapshot = await getDocs(assignmentsQuery);
                const assignmentsData = assignmentsSnapshot.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data() 
                }));
                setAssignments(assignmentsData);
                setStats(prev => ({ ...prev, totalAssignments: assignmentsData.length }));

                // Fetch pending submissions count
                const submissionsRef = collection(db, 'institutions', instId, 'submissions');
                const submissionsQuery = query(
                    submissionsRef,
                    where('teacher_id', '==', teacherId),
                    where('status', '==', 'pending')
                );
                const submissionsSnapshot = await getDocs(submissionsQuery);
                setStats(prev => ({ ...prev, pendingSubmissions: submissionsSnapshot.docs.length }));

                setError(null);
            } catch (error) {
                console.error('Error fetching teacher data:', error);
                setError(error.message || 'Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchTeacherData();
    }, []);

    if (loading) {
        return (
            <Layout>
                <Header title="Teacher Dashboard" />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Header title="Teacher Dashboard" />
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, background: '#f8f9fa' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Welcome Section */}
                {teacherData && (
                    <Box sx={{ 
                        mb: 4, 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        color: 'white', 
                        p: 3, 
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                Welcome back, {teacherData.name}! 👋
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                <strong>Department:</strong> {teacherData.department} | <strong>Email:</strong> {teacherData.email}
                            </Typography>
                        </Box>
                        <Box
                            component="img"
                            src={teacherData.avatar}
                            alt={teacherData.name}
                            sx={{ width: 60, height: 60, borderRadius: '50%', border: '3px solid white' }}
                        />
                    </Box>
                )}

                {/* Statistics Cards */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {[
                        { label: 'Total Students', value: stats.totalStudents, icon: <StudentIcon /> },
                        { label: 'My Classes', value: stats.totalClasses, icon: <ClassIcon /> },
                        { label: 'Total Assignments', value: stats.totalAssignments, icon: <AssignmentIcon /> },
                        { label: 'Pending Reviews', value: stats.pendingSubmissions, icon: <PendingActionsIcon /> },
                    ].map((stat, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                            <Card sx={{ p: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>{stat.label}</Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>{stat.value}</Typography>
                                    </Box>
                                    <Box sx={{ fontSize: 40, opacity: 0.7 }}>{stat.icon}</Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: 'h6.fontSize', sm: 'h5.fontSize' }, mb: 3, fontWeight: 600 }}>
                    Quick Actions
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 5 }}>
                    {cards.map((card) => (
                        <Grid item xs={12} sm={6} md={4} key={card.id}>
                            <Link to={card.slug} style={{ textDecoration: 'none' }}>
                                <Card
                                    className='shadow'
                                    sx={{
                                        height: '150px',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 6,
                                        },
                                    }}
                                >
                                    <CardActionArea sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                                            <Box sx={{ mb: 1, opacity: 0.7, color: '#667eea' }}>{card.icon}</Box>
                                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 600 }}>
                                                {card.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {card.description}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Link>
                        </Grid>
                    ))}
                </Grid>

                {/* Classes Section */}
                <Box sx={{ mb: 5 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: 'h6.fontSize', sm: 'h5.fontSize' }, fontWeight: 600 }}>
                        My Classes ({classes.length})
                    </Typography>
                    {classes.length > 0 ? (
                        <Grid container spacing={2}>
                            {classes.map((cls) => (
                                <Grid item xs={12} sm={6} md={4} key={cls.id}>
                                    <Card sx={{ p: 2, borderRadius: 2, transition: 'all 0.3s', '&:hover': { boxShadow: 4 } }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                                {cls.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Typography variant="body2"><strong>Section:</strong> {cls.section || 'N/A'}</Typography>
                                                <Typography variant="body2"><strong>Room:</strong> {cls.room_number || 'N/A'}</Typography>
                                                <Typography variant="body2"><strong>Students:</strong> {cls.total_students || 0}</Typography>
                                                <Typography variant="body2"><strong>Timing:</strong> {cls.timing || 'N/A'}</Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <ClassIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                            <Typography>No classes assigned yet.</Typography>
                        </Card>
                    )}
                </Box>

                {/* Students Section */}
                <Box sx={{ mb: 5 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: 'h6.fontSize', sm: 'h5.fontSize' }, fontWeight: 600 }}>
                        My Students ({students.length})
                    </Typography>
                    {students.length > 0 ? (
                        isMobile ? (
                            // Mobile view - cards
                            <Box>
                                {students.slice(0, 8).map((student) => (
                                    <Card key={student.id} sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {student.name}
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                            <Typography variant="body2"><strong>Roll:</strong> {student.roll_number || 'N/A'}</Typography>
                                            <Typography variant="body2"><strong>Class:</strong> {student.class}</Typography>
                                            <Typography variant="body2" sx={{ gridColumn: '1/-1', fontSize: '0.85rem', color: '#666' }}>
                                                {student.email}
                                            </Typography>
                                            <Chip label={student.status || 'active'} size="small" color="primary" variant="outlined" sx={{ gridColumn: '1/-1' }} />
                                        </Box>
                                    </Card>
                                ))}
                                {students.length > 8 && (
                                    <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mt: 2 }}>
                                        +{students.length - 8} more students
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            // Desktop view - table
                            <TableContainer className='shadow' component={Paper} sx={{ overflowX: 'auto', borderRadius: 2 }}>
                                <Table>
                                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Roll No.</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {students.map((student, index) => (
                                            <TableRow key={student.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>{student.name}</TableCell>
                                                <TableCell>{student.roll_number || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Chip label={student.class} size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell>{student.email}</TableCell>
                                                <TableCell>{student.phone || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={student.status || 'active'} 
                                                        size="small" 
                                                        color={student.status === 'active' ? 'success' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : (
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <StudentIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                            <Typography>No students assigned to your classes yet.</Typography>
                        </Card>
                    )}
                </Box>

                {/* Assignments Section */}
                <Box>
                    <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: 'h6.fontSize', sm: 'h5.fontSize' }, fontWeight: 600 }}>
                        Recent Assignments ({assignments.length})
                    </Typography>
                    {assignments.length > 0 ? (
                        <Grid container spacing={2}>
                            {assignments.slice(0, 6).map((assignment) => (
                                <Grid item xs={12} sm={6} md={4} key={assignment.id}>
                                    <Card sx={{ height: '100%', borderRadius: 2, transition: 'all 0.3s', '&:hover': { boxShadow: 4 } }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
                                                {assignment.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '40px' }}>
                                                {assignment.description?.substring(0, 60)}...
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                                <Chip 
                                                    label={`Due: ${assignment.dueDate ? new Date(assignment.dueDate?.seconds ? assignment.dueDate.seconds * 1000 : assignment.dueDate).toLocaleDateString() : 'N/A'}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip 
                                                    label={`${assignment.subject || 'General'}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <AssignmentIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                            <Typography>No assignments created yet. Start by creating a new assignment.</Typography>
                        </Card>
                    )}
                </Box>
            </Box>
        </Layout>
    );
}

export default TeacherDashboard;

